import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Film, Image as ImageIcon, ChevronRight } from "lucide-react";
import { fileUrl } from "../pages/VideoExplicative";

function isVideo(ct) { return (ct || "").startsWith("video/"); }
function isImage(ct) { return (ct || "").startsWith("image/"); }

/**
 * Card pentru Dashboard care arată miniaturile celor mai recente fișiere
 * încărcate în modulul VIDEO EXPLICATIVE. Click pe miniatură → deschide pagina
 * completă. Vizibil pentru toți utilizatorii autentificați.
 */
export default function VideoExplicativeCard({ limit = 8 }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/video-explicative");
        if (!cancelled) setItems((r.data || []).slice(0, limit));
      } catch {
        // silent — afișăm starea goală dacă nu putem încărca
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  return (
    <div className="card-flat p-4" data-testid="dashboard-video-explicative-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Film size={18} className="text-[var(--primary)]" />
          <h3 className="font-display font-extrabold text-base">Video explicative</h3>
        </div>
        <button
          type="button"
          onClick={() => navigate("/video-explicative")}
          className="text-xs text-[var(--primary)] font-semibold hover:underline inline-flex items-center gap-1"
          data-testid="dashboard-video-explicative-open"
        >
          Vezi toate <ChevronRight size={12}/>
        </button>
      </div>
      {loading ? (
        <div className="text-xs text-zinc-500 italic">Se încarcă…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-zinc-500 italic py-4 text-center">Niciun fișier încă.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="dashboard-video-explicative-grid">
          {items.map((it) => (
            <button
              type="button"
              key={it.id}
              onClick={() => navigate("/video-explicative")}
              className="aspect-video bg-zinc-100 rounded overflow-hidden relative group hover:ring-2 hover:ring-[var(--primary)] transition-all"
              title={it.original_filename}
              data-testid={`dashboard-video-thumb-${it.id}`}
            >
              {isImage(it.content_type) ? (
                <img src={fileUrl(it.id)} alt={it.original_filename} className="w-full h-full object-cover" loading="lazy" />
              ) : isVideo(it.content_type) ? (
                <video src={fileUrl(it.id)} className="w-full h-full object-cover" preload="none" muted />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-400 text-[10px]">Fișier</div>
              )}
              <span className="absolute top-1 left-1 bg-black/60 text-white text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded inline-flex items-center gap-0.5">
                {isVideo(it.content_type) ? <><Film size={8}/> Video</> : isImage(it.content_type) ? <><ImageIcon size={8}/> Foto</> : "Fișier"}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
